import { Colors, Lightning, Storage } from "@lightningjs/sdk";
import { TranslatableText } from "../../../../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../../../../components/input/Button/Button";
import theme from "../../../../../lib/theme";
import { isPostTvLinux, isPostTvAndroid, STORAGE_KEYS } from "../../../../../lib/utils";
import { postTvWebSocket } from "../../../../../lib/websocket";

const BOX_SIZE = 64;
const BOX_SPACING = 16;

// PinBox component for individual PIN input boxes
interface PinBoxTemplateSpec extends Lightning.Component.TemplateSpec {
    Background?: object;
    Overlay?: object;
}

class PinBox
    extends Lightning.Component<PinBoxTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PinBoxTemplateSpec>
{
    private _value: string = "0";
    private _masked: boolean = false;

    static override _template(): Lightning.Component.Template<PinBoxTemplateSpec> {
        return {
            w: BOX_SIZE,
            h: BOX_SIZE,
            Background: {
                x: -1,
                y: -1,
                texture: Lightning.Tools.getRoundRect(BOX_SIZE, BOX_SIZE, 10, 0, 0xffffffff, true, 0xffffffff)
            },
            Overlay: {
                w: BOX_SIZE,
                h: BOX_SIZE
            }
        };
    }

    override _init() {
        this._renderContent();
    }

    setValue(val: string, masked: boolean) {
        this._value = val;
        this._masked = masked;
        this._renderContent();
    }

    _renderContent() {
        const overlay = this.getByRef("Overlay") as any;
        if (!overlay) return;

        if (this._masked) {
            // Show blue dot centered in box
            overlay.patch({
                x: 0,
                y: 0,
                w: BOX_SIZE,
                h: BOX_SIZE,
                mount: 0,
                text: undefined,
                texture: undefined
            });
            // Use a child for the dot to avoid property conflicts
            overlay.children = [{
                ref: "Dot",
                x: (BOX_SIZE - 20) / 2,
                y: (BOX_SIZE - 20) / 2,
                w: 20,
                h: 20,
                texture: Lightning.Tools.getRoundRect(20, 20, 10, 0, undefined, true, 0xff1565c0)
            }];
        } else {
            // Show text centered in box
            overlay.patch({
                x: 0,
                y: 0,
                w: BOX_SIZE,
                h: BOX_SIZE,
                mount: 0,
                texture: undefined,
                text: undefined
            });
            overlay.children = [{
                ref: "Text",
                x: BOX_SIZE / 2,
                y: BOX_SIZE / 2 + 5,
                mountX: 0.5,
                mountY: 0.5,
                text: {
                    text: this._value,
                    fontSize: 34,
                    textColor: 0xff1565c0,
                    textAlign: "center"
                }
            }];
        }
    }

    override _focus() {
        (this.getByRef("Background") as any).patch({
            texture: Lightning.Tools.getRoundRect(BOX_SIZE, BOX_SIZE, 10, 3, 0xffffffff, true, 0xffffffff)
        });
    }

    override _unfocus() {
        (this.getByRef("Background") as any).patch({
            texture: Lightning.Tools.getRoundRect(BOX_SIZE, BOX_SIZE, 10, 0, 0xffffffff, true, 0xffffffff)
        });
    }
}

interface SettingsPinContentTemplateSpec extends Lightning.Component.TemplateSpec {
    Title: typeof TranslatableText;
    Subtitle: typeof TranslatableText;
    PinBoxes: {
        Box1: typeof PinBox;
        Box2: typeof PinBox;
        Box3: typeof PinBox;
        Box4: typeof PinBox;
    };
    Error: typeof TranslatableText;
    ContinueWrapper: {
        Border: object;
        Continue: typeof Button;
    };
    SuccessView: {
        Icon: object;
        Message: typeof TranslatableText;
    };
}

export class SettingsPinContent
    extends Lightning.Component<SettingsPinContentTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsPinContentTemplateSpec>
{
    private _pin: string[] = [];
    private _focusedBoxIndex: number = 0;
    private _onSuccess?: () => void;
    private _onCancel?: () => void;
    private _onPinSet?: (pin: string) => void;
    private _isVerifying: boolean = false;
    private _mode: "verify" | "set" | "confirm" | "age" = "verify";
    private _firstPin?: string;
    private _digitCount: number = 4;
    private _successKey: string = "pin.changeSuccess";

    readonly Title = this.getByRef("Title")!;
    readonly Subtitle = this.getByRef("Subtitle")!;
    readonly PinBoxes = this.getByRef("PinBoxes")!;
    readonly Error = this.getByRef("Error")!;
    readonly ContinueWrapper = this.getByRef("ContinueWrapper")!;
    readonly Continue = this.ContinueWrapper.getByRef("Continue")!;
    readonly SuccessView = this.getByRef("SuccessView")!;

    static override _template(): Lightning.Component.Template<SettingsPinContentTemplateSpec> {
        const totalWidth4 = BOX_SIZE * 4 + BOX_SPACING * 3;

        return {
            w: 440,
            h: theme.layout.h - 150,
            flex: {
                direction: "column",
                alignItems: "center",
                justifyContent: "center"
            },
            Title: {
                flexItem: { marginBottom: 20 },
                type: TranslatableText,
                key: "pin.title",
                text: {
                    textAlign: "center",
                    fontFace: "SemiBold",
                    fontSize: 32,
                    textColor: Colors(theme.colors.text.primary).get(),
                    wordWrap: true,
                    wordWrapWidth: 400
                }
            },
            Subtitle: {
                flexItem: { marginBottom: 0 },
                h: 0,
                type: TranslatableText,
                key: "",
                text: {
                    textAlign: "center",
                    fontSize: 24,
                    textColor: Colors(theme.colors.text.secondary).get(),
                    wordWrap: true,
                    wordWrapWidth: 400
                },
                alpha: 0
            },
            PinBoxes: {
                flexItem: { marginBottom: 16 },
                flex: {
                    direction: "row",
                    alignItems: "center",
                    justifyContent: "center"
                },
                w: totalWidth4,
                Box1: {
                    type: PinBox,
                    flexItem: { marginRight: BOX_SPACING }
                },
                Box2: {
                    type: PinBox,
                    flexItem: { marginRight: BOX_SPACING }
                },
                Box3: {
                    type: PinBox,
                    flexItem: { marginRight: BOX_SPACING }
                },
                Box4: {
                    type: PinBox
                }
            },
            Error: {
                type: TranslatableText,
                key: "pin.enter",
                h: 28,
                text: {
                    textAlign: "center",
                    fontSize: 18,
                    textColor: Colors("#ff0000").get()
                },
                alpha: 0
            },
            ContinueWrapper: {
                flexItem: { marginTop: 16 },
                w: 300,
                h: (theme.button.h || 56) + 2,
                Continue: {
                    type: Button,
                    w: 300,
                    label: "pin.continue",
                    actionId: "continue"
                }
            },
            SuccessView: {
                alpha: 0,
                visible: false,
                flex: {
                    direction: "column",
                    alignItems: "center",
                    justifyContent: "center"
                },
                w: 440,
                flexItem: { marginTop: 40 },
                Icon: {
                    w: 80,
                    h: 80,
                    Circle: {
                        w: 80,
                        h: 80,
                        rect: true,
                        color: 0xffffffff,
                        shader: {
                            type: Lightning.shaders.RoundedRectangle,
                            radius: 40
                        }
                    },
                    Check: {
                        x: 14,
                        y: 14,
                        zIndex: 2,
                        w: 52,
                        h: 52
                    }
                },
                Message: {
                    flexItem: { marginTop: 20 },
                    type: TranslatableText,
                    key: "pin.changeSuccess",
                    text: {
                        textAlign: "center",
                        fontSize: 24,
                        textColor: Colors(theme.colors.text.primary).get(),
                        wordWrap: true,
                        wordWrapWidth: 400
                    }
                }
            }
        };
    }

    override _init() {
        this._updatePinBoxes();
        this._setState("PinBoxesState");
        this._patchButtonBorder();
    }

    _patchButtonBorder() {
        const btn = this.Continue as any;
        if (!btn || !btn._createBackground) return;

        const original = btn._createBackground.bind(btn);
        btn._createBackground = () => {
            original();
            // Re-patch the RoundedRectangle to include white border
            const hasFocus = btn.hasFocus();
            btn.patch({
                RoundedRectangle: {
                    x: -1,
                    y: -1,
                    texture: Lightning.Tools.getRoundRect(
                        btn.w,
                        btn.h || theme.button.h || 56,
                        theme.button.radius || 8,
                        2,
                        0xffffffff,
                        hasFocus,
                        hasFocus ? btn._getBtnColor() : 0x00000000
                    ),
                    alpha: 1
                }
            });
        };
        // Run it once immediately
        btn._createBackground();
    }

    static override _states() {
        return [
            class PinBoxesState extends this {
                override _getFocused() {
                    return this.PinBoxes.getByRef(`Box${this._focusedBoxIndex + 1}` as any);
                }

                override _handleDown() {
                    if (this._isVerifying) return false;
                    this._setState("ContinueState");
                    return true;
                }

                override _handleBack() {
                    this._returnFocusToList();
                    return true;
                }

                override _handleLeft() {
                    this._returnFocusToList();
                    return true;
                }

                override _handleKey(event: KeyboardEvent) {
                    if (this._isVerifying) return false;

                    const key = event.key;
                    const numKey = +key;
                    const maxLength = this._digitCount;

                    if (!isNaN(numKey) && numKey >= 0 && numKey <= 9 && this._pin.length < maxLength) {
                        this._pin.push(key);
                        this._focusedBoxIndex = this._pin.length;
                        this._updatePinBoxes();
                        this._updateSubmitButton();
                        this._hideError();

                        if (this._mode === "age" && this._pin.length >= this._digitCount) {
                            this._setState("ContinueState");
                        } else if (this._pin.length === this._digitCount) {
                            this._setState("ContinueState");
                        }
                    }

                    if (key === "Backspace" || key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._updateSubmitButton();
                            this._hideError();
                        }
                    }
                }
            },
            class ContinueState extends this {
                override _getFocused() {
                    return this.Continue;
                }

                override _handleUp() {
                    if (this._isVerifying) return;
                    this._setState("PinBoxesState");
                }

                override _handleBack() {
                    this._returnFocusToList();
                    return true;
                }

                override _handleLeft() {
                    this._returnFocusToList();
                    return true;
                }

                override _handleKey(event: KeyboardEvent) {
                    if (this._isVerifying) return;
                    const key = event.key;
                    if (key === "Backspace" || key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._updateSubmitButton();
                            this._hideError();
                            this._setState("PinBoxesState");
                        }
                    }
                }
            }
        ];
    }

    _returnFocusToList() {
        this.fireAncestors("$unfocusContent");
    }

    $handleBtnClick(id: string) {
        if (id === "continue") {
            this._onContinueClick();
        }
    }

    _onContinueClick() {
        let isComplete = false;
        if (this._mode === "age") {
            isComplete = this._pin.length >= 1 && this._pin.length <= this._digitCount;
        } else {
            isComplete = this._pin.length === this._digitCount;
        }
        if (isComplete) {
            this._verifyPin();
        }
    }

    override _captureKey(e: KeyboardEvent) {
        if (e.key === "Enter" || e.keyCode === 13) {
            const state = this._getState();
            if (state === "ContinueState") {
                this._onContinueClick();
                return true;
            }
        }
        return false;
    }

    open(
        onSuccess?: () => void,
        onCancel?: () => void,
        mode: "verify" | "set" | "confirm" | "age" = "verify",
        onPinSet?: (pin: string) => void,
        digitCount: number = 4,
        successKey: string = "pin.changeSuccess"
    ) {
        this._onSuccess = onSuccess;
        this._onCancel = onCancel;
        this._mode = mode;
        this._onPinSet = onPinSet;
        this._digitCount = digitCount;
        this._successKey = successKey;
        this._pin = [];
        this._focusedBoxIndex = 0;
        this._isVerifying = false;
        this._firstPin = undefined;

        // Hide success view, show input elements
        this._hideSuccess();

        // Update title based on mode
        if (mode === "set") {
            this.Title.patch({ key: "pin.setTitle" });
            this.Subtitle.patch({ alpha: 0, h: 0, flexItem: { marginBottom: 0 } });
        } else if (mode === "confirm") {
            this.Title.patch({ key: "pin.confirmTitle" });
            this.Subtitle.patch({ alpha: 0, h: 0, flexItem: { marginBottom: 0 } });
        } else if (mode === "age") {
            this.Title.patch({ key: "pin.ageTitle" });
            this.Subtitle.patch({ alpha: 0, h: 0, flexItem: { marginBottom: 0 } });
        } else {
            this.Title.patch({ key: "pin.title" });
            this.Subtitle.patch({ key: "pin.enterCurrentToChange", alpha: 1, h: undefined, flexItem: { marginBottom: 28 } });
        }

        this._updatePinBoxes();
        this._updateSubmitButton();
        this._hideError();
        this._setState("PinBoxesState");
    }

    reset() {
        this._pin = [];
        this._focusedBoxIndex = 0;
        this._isVerifying = false;
        this._mode = "verify";
        this._onPinSet = undefined;
        this._onSuccess = undefined;
        this._onCancel = undefined;
        this._firstPin = undefined;
        this._hideSuccess();
        this._updatePinBoxes();
        this._updateSubmitButton();
        this._hideError();
        this._setState("PinBoxesState");
    }

    _updatePinBoxes() {
        const maxBoxes = this._digitCount;
        const totalWidth = BOX_SIZE * maxBoxes + BOX_SPACING * (maxBoxes - 1);
        this.PinBoxes.patch({ w: totalWidth });

        const showNumbers = this._mode === "age";

        for (let i = 0; i < 4; i++) {
            const box = this.PinBoxes.getByRef(`Box${i + 1}` as any) as PinBox;
            if (box) {
                const hasValue = i < this._pin.length;
                const shouldShow = i < maxBoxes;

                if (showNumbers && hasValue) {
                    box.setValue(this._pin[i] || "0", false);
                } else if (hasValue) {
                    box.setValue("", true);
                } else {
                    box.setValue("0", false);
                }

                box.patch({
                    alpha: shouldShow ? 1 : 0,
                    visible: shouldShow
                });
            }
        }
        this._updateSubmitButton();
    }

    _updateSubmitButton() {
        let isComplete = false;
        if (this._mode === "age") {
            isComplete = this._pin.length >= 1 && this._pin.length <= this._digitCount;
        } else {
            isComplete = this._pin.length === this._digitCount;
        }
        this.Continue.disabled = !isComplete;
    }

    _hideError() {
        this.Error.patch({ alpha: 0, key: "pin.enter" });
    }

    _showError() {
        this.Error.patch({ alpha: 1 });
    }

    _showSuccess(key?: string) {
        // Hide PIN input elements
        this.Title.patch({ alpha: 0 });
        this.Subtitle.patch({ alpha: 0 });
        this.PinBoxes.patch({ alpha: 0, visible: false });
        this.Error.patch({ alpha: 0 });
        this.ContinueWrapper.patch({ alpha: 0, visible: false });

        // Show success view
        const messageKey = key || this._successKey;
        const messageRef = this.SuccessView.getByRef("Message") as any;
        if (messageRef) {
            messageRef.patch({ key: messageKey });
        }

        // Load checkmark SVG texture
        const iconContainer = this.SuccessView.getByRef("Icon") as any;
        if (iconContainer) {
            const checkEl = iconContainer.getByRef("Check") as any;
            if (checkEl) {
                const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="52" height="52"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#1565C0"/></svg>`;
                const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
                checkEl.patch({
                    texture: Lightning.Tools.getSvgTexture(dataUrl, 52, 52)
                });
            }
        }

        this.SuccessView.patch({ alpha: 1, visible: true });
    }

    _hideSuccess() {
        this.SuccessView.patch({ alpha: 0, visible: false });
        this.Title.patch({ alpha: 1 });
        this.PinBoxes.patch({ alpha: 1, visible: true });
        this.ContinueWrapper.patch({ alpha: 1, visible: true });
    }

    async _verifyPin() {
        let isValidLength = false;
        if (this._mode === "age") {
            isValidLength = this._pin.length >= 1 && this._pin.length <= this._digitCount;
        } else {
            isValidLength = this._pin.length === this._digitCount;
        }

        if (!isValidLength) {
            this._showError();
            return;
        }

        this._isVerifying = true;
        const pin = this._pin.join("");

        // Mode: set → store PIN, switch to confirm
        if (this._mode === "set") {
            this._isVerifying = false;
            this._firstPin = pin;
            this._pin = [];
            this._focusedBoxIndex = 0;
            this._updatePinBoxes();
            this._mode = "confirm";
            this.Title.patch({ key: "pin.confirmTitle" });
            this._hideError();
            this._setState("PinBoxesState");
            setTimeout(() => {
                this._refocus();
            }, 50);
            return;
        }

        // Mode: age → validate and return
        if (this._mode === "age") {
            this._isVerifying = false;
            const age = parseInt(pin, 10);
            if (isNaN(age) || age < 0 || age > 99) {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._updatePinBoxes();
                this.Error.patch({ key: "pin.invalidAge" });
                this._showError();
                this._setState("PinBoxesState");
                return;
            }
            const ageString = age < 10 ? `0${age}` : `${age}`;
            this._onPinSet?.(ageString);
            this._onSuccess?.();
            return;
        }

        // Mode: confirm → compare with first PIN
        if (this._mode === "confirm") {
            this._isVerifying = false;
            if (pin === this._firstPin) {
                this._onPinSet?.(pin);
                this._onSuccess?.();
            } else {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._firstPin = undefined;
                this._mode = "set";
                this._updatePinBoxes();
                this.Error.patch({ key: "pin.mismatch" });
                this._showError();
                this.Title.patch({ key: "pin.setTitle" });
                this._setState("PinBoxesState");
            }
            return;
        }

        // Mode: verify → check against stored PIN
        try {
            let isValid = false;

            const user = Storage.get(STORAGE_KEYS.user) as any;
            const storedPin = user?.settings?.parentalPin || Storage.get(STORAGE_KEYS.parentalPin);

            if (storedPin) {
                isValid = String(storedPin) === String(pin);
            }

            if (!isValid && !storedPin) {
                if (isPostTvLinux()) {
                    isValid = await new Promise<boolean>((resolve) => {
                        try {
                            const timeoutId = setTimeout(() => resolve(false), 5000);
                            postTvWebSocket.getSettings("pics.pin", (response) => {
                                clearTimeout(timeoutId);
                                try {
                                    const settingsPin = response?.data?.value || response?.value || "";
                                    resolve(String(settingsPin) === String(pin));
                                } catch (e) {
                                    resolve(false);
                                }
                            });
                        } catch (e) {
                            resolve(false);
                        }
                    });
                } else if (isPostTvAndroid()) {
                    try {
                        const android = window.Android;
                        if (android && typeof android.testPinCode === "function") {
                            const result = android.testPinCode(pin);
                            isValid = typeof result === "boolean" ? result : await Promise.resolve(result);
                        }
                    } catch (e) {
                        isValid = false;
                    }
                }
            }

            this._isVerifying = false;

            if (isValid) {
                this._onSuccess?.();
            } else {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._updatePinBoxes();
                this._showError();
                this._setState("PinBoxesState");
            }
        } catch (e) {
            console.error("[SettingsPinContent] Error verifying PIN:", e);
            this._isVerifying = false;
            this._showError();
        }
    }
}
